import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static MAX_UNIT_PRICE = 9999999999.99


  static values = {
    projectId: Number,
    editing: Boolean,
    errorIconPath: String,
    successIconPath: String,
    closeIconPath: String
  }

  static targets = [
    "row",
    "unitPriceInput",
    "noteInput",
    "unitPriceDisplay",
    "noteDisplay",
    "quantityDisplay",
    "totalPriceDisplay",
    "editButton",
    "saveButton",
    "cancelButton",
    "exportButton"
  ]

  connect() {
    this.setEditing(false)
    this._initInputFiltering()
  }

  _initInputFiltering() {
    this.unitPriceInputTargets.forEach(input => {
      // Block the minus sign key immediately
      input.addEventListener('keydown', (e) => {
        if (e.key === '-' || e.key === 'e') {
          e.preventDefault()
        }
      })

      input.addEventListener('input', (e) => {
        const val = e.target.value
        // Only allow numbers and a single period, strip anything else
        const cleaned = val.replace(/[^0-9.]/g, '')
        
        // Handle multiple decimal points
        const parts = cleaned.split('.')
        let final = parts[0]
        if (parts.length > 1) {
          final += '.' + parts.slice(1).join('')
        }
        
        const numeric = Number(final)
        if (val !== final) {
          e.target.value = final
        }

        const current = e.target.value?.trim() || ""
        const numberValue = Number(current)
        const validScale = /^\d+(\.\d{1,2})?$/.test(current)
        const invalid = current !== "" && (!Number.isFinite(numberValue) || numberValue < 0 || numberValue > this.constructor.MAX_UNIT_PRICE || !validScale)
        e.target.classList.toggle("bom__inline-input--error", invalid)
      })
    })
  }

  showToast(message, type = 'info') {
    const notification = document.createElement('div')
    notification.className = `toast__content ${type === 'error' ? 'toast__content--error' : ''}`

    const icon = document.createElement('img')
    icon.width = 16
    icon.height = 16
    icon.alt = type === 'error' ? 'error' : 'success'
    icon.src = type === 'error' ? this.errorIconPathValue : this.successIconPathValue
    notification.appendChild(icon)

    const messageSpan = document.createElement('span')
    messageSpan.className = 'toast__message'
    messageSpan.textContent = message
    notification.appendChild(messageSpan)

    const action = document.createElement('div')
    action.className = 'toast__action'
    const close = document.createElement('img')
    close.width = 9
    close.height = 9
    close.alt = 'close'
    close.className = 'toast__close'
    close.src = this.closeIconPathValue
    close.addEventListener('click', () => notification.remove())
    action.appendChild(close)
    notification.appendChild(action)

    let container = document.querySelector('.toast__container')
    if (!container) {
      container = document.createElement('div')
      container.className = 'toast__container'
      document.body.appendChild(container)
    }

    container.appendChild(notification)

    setTimeout(() => {
      if (notification.parentNode) {
        notification.parentNode.removeChild(notification)
      }
    }, 10000)
  }

  toggleEdit(event) {
    event.preventDefault()
    this.setEditing(true)
  }

  setEditing(editing) {
    this.editingValue = !!editing

    this.unitPriceInputTargets.forEach((input) => {
      input.classList.toggle("hidden", !this.editingValue)
      input.disabled = !this.editingValue
    })

    this.noteInputTargets.forEach((input) => {
      input.classList.toggle("hidden", !this.editingValue)
      input.disabled = !this.editingValue
    })

    this.unitPriceDisplayTargets.forEach((el) => {
      el.classList.toggle("hidden", this.editingValue)
    })

    this.noteDisplayTargets.forEach((el) => {
      el.classList.toggle("hidden", this.editingValue)
    })

    if (this.hasExportButtonTarget) {
      this.exportButtonTarget.classList.toggle("hidden", this.editingValue)
    }
    if (this.hasEditButtonTarget) {
      this.editButtonTarget.classList.toggle("hidden", this.editingValue)
    }
    if (this.hasSaveButtonTarget) {
      this.saveButtonTarget.classList.toggle("hidden", !this.editingValue)
    }
    if (this.hasCancelButtonTarget) {
      this.cancelButtonTarget.classList.toggle("hidden", !this.editingValue)
    }
  }

  async save(event) {
    event.preventDefault()
    const { changes, validationErrors } = this._collectChanges()

    if (validationErrors.length > 0) {
      const hasUnitPriceOverLimit = validationErrors.some((msg) =>
        msg.includes("Unit Price cannot exceed")
      )

      if (hasUnitPriceOverLimit) {
        this.showToast(
          `Cannot save. Unit price must not exceed ${this.constructor.MAX_UNIT_PRICE}.`,
          "error"
        )
      } else {
        this.showToast(`Save failed: ${validationErrors.join("; ")}`, "error")
      }
      return
    }

    if (changes.length === 0) {
      this.setEditing(false)
      return
    }

    const token = document.querySelector("meta[name='csrf-token']")?.content
    try {
      const res = await fetch(`/projects/${this.projectIdValue}/boms/batch_update`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": token
        },
        body: JSON.stringify({ boms: changes })
      })

      if (!res.ok) {
        const data = await res.json()
        const errMsg = data.errors?.map(e => e.error).join("; ") || `Status ${res.status}`
        throw new Error(errMsg)
      }

      this._applyChanges(changes)
      this.setEditing(false)
    } catch (error) {
      console.error(error)
      this.showToast(`Save failed: ${error.message}`, "error")
    }
  }

  _collectChanges() {
    const changes = []
    const validationErrors = []
    this.rowTargets.forEach((row) => {
      const id = row.dataset.bomId
      if (!id) return

      const unitInput = row.querySelector('[data-bom-editor-target="unitPriceInput"]')
      const noteInput = row.querySelector('[data-bom-editor-target="noteInput"]')
      if (!unitInput || !noteInput) return

      const rawUnitPrice = unitInput.value?.trim() || ""
      const unitPrice = Number(rawUnitPrice)
      const rowLabel = this._rowLabel(row)

      unitInput.classList.remove("bom__inline-input--error")
      if (rawUnitPrice === "" || !Number.isFinite(unitPrice)) {
        unitInput.classList.add("bom__inline-input--error")
        validationErrors.push(`${rowLabel}: Unit Price must be a valid number`)
        return
      }
      if (!/^\d+(\.\d{1,2})?$/.test(rawUnitPrice)) {
        unitInput.classList.add("bom__inline-input--error")
        validationErrors.push(`${rowLabel}: Unit Price must have at most 2 decimal places`)
        return
      }
      if (unitPrice < 0) {
        unitInput.classList.add("bom__inline-input--error")
        validationErrors.push(`${rowLabel}: Unit Price cannot be negative`)
        return
      }
      if (unitPrice > this.constructor.MAX_UNIT_PRICE) {
        unitInput.classList.add("bom__inline-input--error")
        validationErrors.push(`${rowLabel}: Unit Price cannot exceed ${this.constructor.MAX_UNIT_PRICE.toFixed(2)}`)
        return
      }

      const note = noteInput.value
      const originalUnit = parseFloat(unitInput.dataset.original) || 0
      const originalNote = noteInput.dataset.original

      if (String(unitPrice) !== String(originalUnit) || String(note) !== String(originalNote)) {
        changes.push({
          id,
          unit_price: rawUnitPrice,
          note
        })
      }
    })
    return { changes, validationErrors }
  }

  _applyChanges(changes) {
    const byId = new Map(changes.map((c) => [String(c.id), c]))
    this.rowTargets.forEach((row) => {
      const id = row.dataset.bomId
      const data = byId.get(String(id))
      if (!data) return

      const unitInput = row.querySelector('[data-bom-editor-target="unitPriceInput"]')
      const noteInput = row.querySelector('[data-bom-editor-target="noteInput"]')
      const unitDisplay = row.querySelector('[data-bom-editor-target="unitPriceDisplay"]')
      const noteDisplay = row.querySelector('[data-bom-editor-target="noteDisplay"]')
      const quantityDisplay = row.querySelector('[data-bom-editor-target="quantityDisplay"]')
      const totalDisplay = row.querySelector('[data-bom-editor-target="totalPriceDisplay"]')

      if (unitInput) unitInput.dataset.original = data.unit_price
      if (noteInput) noteInput.dataset.original = data.note
      if (unitDisplay) {
        const formatted = this._formatNumber(data.unit_price)
        unitDisplay.textContent = formatted === "" ? String(data.unit_price || "-") : formatted
      }
      if (noteDisplay) noteDisplay.textContent = data.note || "-"
      if (quantityDisplay && totalDisplay) {
        const quantity = parseFloat(quantityDisplay.textContent)
        const unitPrice = parseFloat(data.unit_price)
        if (Number.isFinite(quantity) && Number.isFinite(unitPrice)) {
          totalDisplay.textContent = this._formatNumber(unitPrice * quantity)
        }
      }
    })
  }

  _formatNumber(value) {
    const num = Number(value)
    if (!Number.isFinite(num)) return ""
    const fixed = num.toFixed(2)
    const trimmed = fixed.replace(/0+$/, "").replace(/\.$/, ".0")
    return trimmed
  }

  _rowLabel(row) {
    const modelText = row.querySelector("td:nth-child(3)")?.textContent?.trim()
    return modelText ? `Device Model ${modelText}` : `BOM item ${row.dataset.bomId || "-"}`
  }
}
